import { Pipe, PipeTransform, inject } from '@angular/core';
import type { PlanAddons, PlanMasterList, ReportListResponse, Settings, AutomationMaster } from '@fitness-force/models';
import { TranslateService } from '@ngx-translate/core';

@Pipe({
  name: 'settingsFilter',
  standalone: true
})
export class SettingsFilterPipe implements PipeTransform {
  private readonly translate = inject(TranslateService);

  transform(settings: Settings[], term: string): any {
    //check if the search term is defined
    if (!settings || !term) return settings;
    
    //return updated setting array
    const result = settings.filter(s => {
      const translatedSettingName = this.translate.instant(this.getSettingNameKey(s.shortname));
      
      return s.name.toLowerCase().includes(term.toLowerCase()) ||
             translatedSettingName.toLowerCase().includes(term.toLowerCase()) ||
             s.categories.some(c => {
               const translatedCatName = this.translate.instant(this.getSubsettingNameKey(c.name));
               const translatedCatDesc = this.translate.instant(this.getSubsettingDescriptionKey(c.name));
               
               return c.name.toLowerCase().includes(term.toLowerCase()) ||
                      c.description.toLowerCase().includes(term.toLowerCase()) ||
                      translatedCatName.toLowerCase().includes(term.toLowerCase()) ||
                      translatedCatDesc.toLowerCase().includes(term.toLowerCase());
             });
    });
    return result;
  }

  private getSettingNameKey(shortname: string): string {
    const keyMap: { [key: string]: string } = {
      // Admin Settings
      'scheduling': 'ADMIN_SETTINGS.CATEGORIES.SCHEDULING.NAME',
      'finance_payment': 'ADMIN_SETTINGS.CATEGORIES.FINANCE_PAYMENT.NAME',
      'communication': 'ADMIN_SETTINGS.CATEGORIES.COMMUNICATION.NAME',
      'crm': 'ADMIN_SETTINGS.CATEGORIES.CRM.NAME',
      'general': 'ADMIN_SETTINGS.CATEGORIES.GENERAL.NAME',
      'rate_card': 'ADMIN_SETTINGS.CATEGORIES.RATE_CARD.NAME',
      'users': 'ADMIN_SETTINGS.CATEGORIES.USERS.NAME',
      'security': 'ADMIN_SETTINGS.CATEGORIES.SECURITY.NAME',
      'trial': 'ADMIN_SETTINGS.CATEGORIES.TRIAL.NAME',
      'Partners': 'ADMIN_SETTINGS.CATEGORIES.PARTNERS.NAME',
      'Coupons': 'ADMIN_SETTINGS.CATEGORIES.COUPONS.NAME',
      'organization': 'ADMIN_SETTINGS.CATEGORIES.ORGANIZATION.NAME',
      'integration_master': 'ADMIN_SETTINGS.CATEGORIES.INTEGRATION_MASTER.NAME',
      'Payengine': 'ADMIN_SETTINGS.CATEGORIES.PAYENGINE.NAME',
      'waiver': 'ADMIN_SETTINGS.CATEGORIES.WAIVER.NAME',
      'webhook': 'ADMIN_SETTINGS.CATEGORIES.WEBHOOK.NAME',
      'widgets': 'ADMIN_SETTINGS.CATEGORIES.WIDGETS.NAME',
      'freeze-reason': 'ADMIN_SETTINGS.CATEGORIES.FREEZE_REASON.NAME',
      'cancellation-reason': 'ADMIN_SETTINGS.CATEGORIES.CANCELLATION_REASON.NAME',
      'gl_code_account': 'ADMIN_SETTINGS.CATEGORIES.GL_CODE_ACCOUNT.NAME',
      'commission_management': 'ADMIN_SETTINGS.CATEGORIES.COMMISSION_MANAGEMENT.NAME',
      'forms_surveys': 'ADMIN_SETTINGS.CATEGORIES.FORMS_SURVEYS.NAME',
      // Enterprise Settings
      'plan_master': 'ENTERPRISE_SETTINGS.CATEGORIES.ENTERPRISE_MASTER.NAME'
    };
    return keyMap[shortname] || shortname;
  }

  private getSubsettingNameKey(name: string): string {
    const nameMap: { [key: string]: string } = {
      // Admin Settings - Scheduling
      'Classes': 'ADMIN_SETTINGS.CATEGORIES.SCHEDULING.CLASSES.NAME',
      'Appointment': 'ADMIN_SETTINGS.CATEGORIES.SCHEDULING.APPOINTMENT.NAME',
      'Tags': 'ADMIN_SETTINGS.CATEGORIES.SCHEDULING.TAGS.NAME',
      // Admin Settings - Finance & Payments
      'Payment Modes': 'ADMIN_SETTINGS.CATEGORIES.FINANCE_PAYMENT.PAYMENT_MODES.NAME',
      'Payment Processing': 'ADMIN_SETTINGS.CATEGORIES.FINANCE_PAYMENT.PAYMENT_PROCESSING.NAME',
      'Taxes': 'ADMIN_SETTINGS.CATEGORIES.FINANCE_PAYMENT.TAXES.NAME',
      // Admin Settings - Communications
      'Email Templates': 'ADMIN_SETTINGS.CATEGORIES.COMMUNICATION.EMAIL_TEMPLATES.NAME',
      'SMS Templates': 'ADMIN_SETTINGS.CATEGORIES.COMMUNICATION.SMS_TEMPLATES.NAME',
      'Whatsapp Templates': 'ADMIN_SETTINGS.CATEGORIES.COMMUNICATION.WHATSAPP_TEMPLATES.NAME',
      'Push Notification Templates': 'ADMIN_SETTINGS.CATEGORIES.COMMUNICATION.PUSH_NOTIFICATION_TEMPLATES.NAME',
      'Communication Categories': 'ADMIN_SETTINGS.CATEGORIES.COMMUNICATION.COMMUNICATION_CATEGORIES.NAME',
      // Admin Settings - CRM
      'Tasks': 'ADMIN_SETTINGS.CATEGORIES.CRM.TASKS.NAME',
      'Automation': 'ADMIN_SETTINGS.CATEGORIES.CRM.AUTOMATION.NAME',
      'Document Types': 'ADMIN_SETTINGS.CATEGORIES.CRM.DOCUMENT_TYPES.NAME',
      'Configure Board': 'ADMIN_SETTINGS.CATEGORIES.CRM.CONFIGURE_BOARD.NAME',
      // Admin Settings - General
      'Branch Settings': 'ADMIN_SETTINGS.CATEGORIES.GENERAL.BRANCH_SETTINGS.NAME',
      'Field Configurations': 'ADMIN_SETTINGS.CATEGORIES.GENERAL.FIELD_CONFIGURATIONS.NAME',
      'Company Info': 'ADMIN_SETTINGS.CATEGORIES.GENERAL.COMPANY_INFO.NAME',
      'Tenants': 'ADMIN_SETTINGS.CATEGORIES.GENERAL.TENANTS.NAME',
      // Admin Settings - Rate Card
      'Plans': 'ADMIN_SETTINGS.CATEGORIES.RATE_CARD.PLANS.NAME',
      'Charges': 'ADMIN_SETTINGS.CATEGORIES.RATE_CARD.CHARGES.NAME',
      'Addons': 'ADMIN_SETTINGS.CATEGORIES.RATE_CARD.ADDONS.NAME',
      'Family Memberships': 'ADMIN_SETTINGS.CATEGORIES.RATE_CARD.FAMILY_MEMBERSHIPS.NAME',
      'Plan Change Management': 'ADMIN_SETTINGS.CATEGORIES.RATE_CARD.PLAN_CHANGE_MANAGEMENT.NAME',
      // Admin Settings - Users
      'Staff': 'ADMIN_SETTINGS.CATEGORIES.USERS.STAFF.NAME',
      // Admin Settings - Security
      'Audit Trail': 'ADMIN_SETTINGS.CATEGORIES.SECURITY.AUDIT_TRAIL.NAME',
      'Password Settings': 'ADMIN_SETTINGS.CATEGORIES.SECURITY.PASSWORD_SETTINGS.NAME',
      'IP': 'ADMIN_SETTINGS.CATEGORIES.SECURITY.IP.NAME',
      'Staff Permission Template': 'ADMIN_SETTINGS.CATEGORIES.SECURITY.STAFF_PERMISSION_TEMPLATE.NAME',
      // Admin Settings - Trial
      'Trial': 'ADMIN_SETTINGS.CATEGORIES.TRIAL.TRIAL.NAME',
      // Admin Settings - Our Partners
      'Our Partners': 'ADMIN_SETTINGS.CATEGORIES.PARTNERS.OUR_PARTNERS.NAME',
      // Admin Settings - Coupons
      'Coupons': 'ADMIN_SETTINGS.CATEGORIES.COUPONS.COUPONS.NAME',
      // Admin Settings - Organization
      'Organization List': 'ADMIN_SETTINGS.CATEGORIES.ORGANIZATION.ORGANIZATION_LIST.NAME',
      // Admin Settings - Integration Master
      'WhatsApp Integration': 'ADMIN_SETTINGS.CATEGORIES.INTEGRATION_MASTER.WHATSAPP_INTEGRATION.NAME',
      'Facebook Integration': 'ADMIN_SETTINGS.CATEGORIES.INTEGRATION_MASTER.FACEBOOK_INTEGRATION.NAME',
      'SMS Integration': 'ADMIN_SETTINGS.CATEGORIES.INTEGRATION_MASTER.SMS_INTEGRATION.NAME',
      'Zatca Integration': 'ADMIN_SETTINGS.CATEGORIES.INTEGRATION_MASTER.ZATCA_INTEGRATION.NAME',
      // Admin Settings - PayEngine
      'Devices': 'ADMIN_SETTINGS.CATEGORIES.PAYENGINE.DEVICES.NAME',
      // Admin Settings - Waiver
      'Waiver': 'ADMIN_SETTINGS.CATEGORIES.WAIVER.WAIVER.NAME',
      // Admin Settings - Webhook
      'Global Webhook (Company Level)': 'ADMIN_SETTINGS.CATEGORIES.WEBHOOK.GLOBAL_WEBHOOK.NAME',
      'Location Webhook (Current Tenant)': 'ADMIN_SETTINGS.CATEGORIES.WEBHOOK.LOCATION_WEBHOOK.NAME',
      // Admin Settings - Widgets
      'Widgets': 'ADMIN_SETTINGS.CATEGORIES.WIDGETS.WIDGETS.NAME',
      // Admin Settings - Freeze Reason Master
      'Freeze Reason Master': 'ADMIN_SETTINGS.CATEGORIES.FREEZE_REASON.FREEZE_REASON_MASTER.NAME',
      // Admin Settings - GL Code Account
      'GL Code Account': 'ADMIN_SETTINGS.CATEGORIES.GL_CODE_ACCOUNT.GL_CODE_ACCOUNT.NAME',
      // Admin Settings - Commission Management
      'Commission Plans & Rules': 'ADMIN_SETTINGS.CATEGORIES.COMMISSION_MANAGEMENT.COMMISSION_PLANS_RULES.NAME',
      'Staff Commission Assignment': 'ADMIN_SETTINGS.CATEGORIES.COMMISSION_MANAGEMENT.STAFF_COMMISSION_ASSIGNMENT.NAME',
      'Commission Earnings & Payouts': 'ADMIN_SETTINGS.CATEGORIES.COMMISSION_MANAGEMENT.COMMISSION_EARNINGS_PAYOUTS.NAME',
      // Admin Settings - Forms & Surveys
      'Form Templates': 'ADMIN_SETTINGS.CATEGORIES.FORMS_SURVEYS.FORM_TEMPLATES.NAME',
      // Enterprise Settings
      'Enterprise Master List': 'ENTERPRISE_SETTINGS.CATEGORIES.ENTERPRISE_MASTER.ENTERPRISE_MASTER_LIST.NAME'
    };
    return nameMap[name] || name;
  }

  private getSubsettingDescriptionKey(name: string): string {
    const descMap: { [key: string]: string } = {
      // Admin Settings - Scheduling
      'Classes': 'ADMIN_SETTINGS.CATEGORIES.SCHEDULING.CLASSES.DESCRIPTION',
      'Appointment': 'ADMIN_SETTINGS.CATEGORIES.SCHEDULING.APPOINTMENT.DESCRIPTION',
      'Tags': 'ADMIN_SETTINGS.CATEGORIES.SCHEDULING.TAGS.DESCRIPTION',
      // Admin Settings - Finance & Payments
      'Payment Modes': 'ADMIN_SETTINGS.CATEGORIES.FINANCE_PAYMENT.PAYMENT_MODES.DESCRIPTION',
      'Payment Processing': 'ADMIN_SETTINGS.CATEGORIES.FINANCE_PAYMENT.PAYMENT_PROCESSING.DESCRIPTION',
      'Taxes': 'ADMIN_SETTINGS.CATEGORIES.FINANCE_PAYMENT.TAXES.DESCRIPTION',
      // Admin Settings - Communications
      'Email Templates': 'ADMIN_SETTINGS.CATEGORIES.COMMUNICATION.EMAIL_TEMPLATES.DESCRIPTION',
      'SMS Templates': 'ADMIN_SETTINGS.CATEGORIES.COMMUNICATION.SMS_TEMPLATES.DESCRIPTION',
      'Whatsapp Templates': 'ADMIN_SETTINGS.CATEGORIES.COMMUNICATION.WHATSAPP_TEMPLATES.DESCRIPTION',
      'Push Notification Templates': 'ADMIN_SETTINGS.CATEGORIES.COMMUNICATION.PUSH_NOTIFICATION_TEMPLATES.DESCRIPTION',
      'Communication Categories': 'ADMIN_SETTINGS.CATEGORIES.COMMUNICATION.COMMUNICATION_CATEGORIES.DESCRIPTION',
      // Admin Settings - CRM
      'Tasks': 'ADMIN_SETTINGS.CATEGORIES.CRM.TASKS.DESCRIPTION',
      'Automation': 'ADMIN_SETTINGS.CATEGORIES.CRM.AUTOMATION.DESCRIPTION',
      'Document Types': 'ADMIN_SETTINGS.CATEGORIES.CRM.DOCUMENT_TYPES.DESCRIPTION',
      'Configure Board': 'ADMIN_SETTINGS.CATEGORIES.CRM.CONFIGURE_BOARD.DESCRIPTION',
      // Admin Settings - General
      'Branch Settings': 'ADMIN_SETTINGS.CATEGORIES.GENERAL.BRANCH_SETTINGS.DESCRIPTION',
      'Field Configurations': 'ADMIN_SETTINGS.CATEGORIES.GENERAL.FIELD_CONFIGURATIONS.DESCRIPTION',
      'Company Info': 'ADMIN_SETTINGS.CATEGORIES.GENERAL.COMPANY_INFO.DESCRIPTION',
      'Tenants': 'ADMIN_SETTINGS.CATEGORIES.GENERAL.TENANTS.DESCRIPTION',
      // Admin Settings - Rate Card
      'Plans': 'ADMIN_SETTINGS.CATEGORIES.RATE_CARD.PLANS.DESCRIPTION',
      'Charges': 'ADMIN_SETTINGS.CATEGORIES.RATE_CARD.CHARGES.DESCRIPTION',
      'Addons': 'ADMIN_SETTINGS.CATEGORIES.RATE_CARD.ADDONS.DESCRIPTION',
      'Family Memberships': 'ADMIN_SETTINGS.CATEGORIES.RATE_CARD.FAMILY_MEMBERSHIPS.DESCRIPTION',
      'Plan Change Management': 'ADMIN_SETTINGS.CATEGORIES.RATE_CARD.PLAN_CHANGE_MANAGEMENT.DESCRIPTION',
      // Admin Settings - Users
      'Staff': 'ADMIN_SETTINGS.CATEGORIES.USERS.STAFF.DESCRIPTION',
      // Admin Settings - Security
      'Audit Trail': 'ADMIN_SETTINGS.CATEGORIES.SECURITY.AUDIT_TRAIL.DESCRIPTION',
      'Password Settings': 'ADMIN_SETTINGS.CATEGORIES.SECURITY.PASSWORD_SETTINGS.DESCRIPTION',
      'IP': 'ADMIN_SETTINGS.CATEGORIES.SECURITY.IP.DESCRIPTION',
      'Staff Permission Template': 'ADMIN_SETTINGS.CATEGORIES.SECURITY.STAFF_PERMISSION_TEMPLATE.DESCRIPTION',
      // Admin Settings - Trial
      'Trial': 'ADMIN_SETTINGS.CATEGORIES.TRIAL.TRIAL.DESCRIPTION',
      // Admin Settings - Our Partners
      'Our Partners': 'ADMIN_SETTINGS.CATEGORIES.PARTNERS.OUR_PARTNERS.DESCRIPTION',
      // Admin Settings - Coupons
      'Coupons': 'ADMIN_SETTINGS.CATEGORIES.COUPONS.COUPONS.DESCRIPTION',
      // Admin Settings - Organization
      'Organization List': 'ADMIN_SETTINGS.CATEGORIES.ORGANIZATION.ORGANIZATION_LIST.DESCRIPTION',
      // Admin Settings - Integration Master
      'WhatsApp Integration': 'ADMIN_SETTINGS.CATEGORIES.INTEGRATION_MASTER.WHATSAPP_INTEGRATION.DESCRIPTION',
      'Facebook Integration': 'ADMIN_SETTINGS.CATEGORIES.INTEGRATION_MASTER.FACEBOOK_INTEGRATION.DESCRIPTION',
      'SMS Integration': 'ADMIN_SETTINGS.CATEGORIES.INTEGRATION_MASTER.SMS_INTEGRATION.DESCRIPTION',
      'Zatca Integration': 'ADMIN_SETTINGS.CATEGORIES.INTEGRATION_MASTER.ZATCA_INTEGRATION.DESCRIPTION',
      // Admin Settings - PayEngine
      'Devices': 'ADMIN_SETTINGS.CATEGORIES.PAYENGINE.DEVICES.DESCRIPTION',
      // Admin Settings - Waiver
      'Waiver': 'ADMIN_SETTINGS.CATEGORIES.WAIVER.WAIVER.DESCRIPTION',
      // Admin Settings - Webhook
      'Global Webhook (Company Level)': 'ADMIN_SETTINGS.CATEGORIES.WEBHOOK.GLOBAL_WEBHOOK.DESCRIPTION',
      'Location Webhook (Current Tenant)': 'ADMIN_SETTINGS.CATEGORIES.WEBHOOK.LOCATION_WEBHOOK.DESCRIPTION',
      // Admin Settings - Widgets
      'Widgets': 'ADMIN_SETTINGS.CATEGORIES.WIDGETS.WIDGETS.DESCRIPTION',
      // Admin Settings - Freeze Reason Master
      'Freeze Reason Master': 'ADMIN_SETTINGS.CATEGORIES.FREEZE_REASON.FREEZE_REASON_MASTER.DESCRIPTION',
      // Admin Settings - GL Code Account
      'GL Code Account': 'ADMIN_SETTINGS.CATEGORIES.GL_CODE_ACCOUNT.GL_CODE_ACCOUNT.DESCRIPTION',
      // Admin Settings - Commission Management
      'Commission Plans & Rules': 'ADMIN_SETTINGS.CATEGORIES.COMMISSION_MANAGEMENT.COMMISSION_PLANS_RULES.DESCRIPTION',
      'Staff Commission Assignment': 'ADMIN_SETTINGS.CATEGORIES.COMMISSION_MANAGEMENT.STAFF_COMMISSION_ASSIGNMENT.DESCRIPTION',
      'Commission Earnings & Payouts': 'ADMIN_SETTINGS.CATEGORIES.COMMISSION_MANAGEMENT.COMMISSION_EARNINGS_PAYOUTS.DESCRIPTION',
      // Admin Settings - Forms & Surveys
      'Form Templates': 'ADMIN_SETTINGS.CATEGORIES.FORMS_SURVEYS.FORM_TEMPLATES.DESCRIPTION',
      // Enterprise Settings
      'Enterprise Master List': 'ENTERPRISE_SETTINGS.CATEGORIES.ENTERPRISE_MASTER.ENTERPRISE_MASTER_LIST.DESCRIPTION'
    };
    return descMap[name] || name;
  }
}

@Pipe({
  name: 'reportFilter',
  standalone: true
})
export class ReportFilterPipe implements PipeTransform {
  constructor() { }

  transform(reports: ReportListResponse[], term: string): any {
    //check if the search term is defined
    if (!reports || !term) return reports;
    //return updated setting array
    let result = reports.filter(s => s.category.toLowerCase().includes(term.toLowerCase())
      || s.reports.some(c => c.report_key.toLowerCase().includes(term.toLowerCase()))
      || s.reports.some(c => c.report_description?.toLowerCase().includes(term.toLowerCase()))
      || s.reports.some(c => c.translated_name?.toLowerCase().includes(term.toLowerCase()))
      || s.reports.some(c => c.translated_description?.toLowerCase().includes(term.toLowerCase()))
    );
    return result;
  }
}


@Pipe({
  name: 'fevouriteReportFilter',
  standalone: true
})
export class FevouriteReportFilterPipe implements PipeTransform {
  constructor() { }

  transform(reports: any, term: string): any {
    //check if the search term is defined
    if (!reports || !term) return reports;
    //return updated setting array
    const result = reports.filter((s: any) => s.report.report_key.toLowerCase().includes(term.toLowerCase())
      || s.report.report_description?.toLowerCase().includes(term.toLowerCase())
      || s.report.translated_name?.toLowerCase().includes(term.toLowerCase())
      || s.report.translated_description?.toLowerCase().includes(term.toLowerCase())
    );
    return result;
  }
}

@Pipe({
  name: 'highlight',
  standalone: true
})

export class HighlightSearch implements PipeTransform {
  constructor() { }

  transform(value: any, args: any): any {

    if (!args) {
      return value;
    }
    // Match in a case insensitive maneer
    const re = new RegExp(args, 'gi');
    const match = value.match(re);

    // If there's no match, just return the original value.
    if (!match) {
      return value;
    }

    const replacedValue = value.replace(re, "<mark>" + match[0] + "</mark>");
    return replacedValue;
  }
}

@Pipe({
  name: 'planFilter',
  standalone: true
})
export class PlanFilterPipe implements PipeTransform {
  constructor() { }

  transform(plan: any, term: any): PlanMasterList[] {
    //check if the search term is defined
    if (!plan || !term) return plan;
    if (!term.filter && !term.serviceTypeFilter) return plan;

    let result = {} as PlanMasterList[];
    if (term.filter && term.serviceTypeFilter) {
      result = plan.filter((s: any) =>
        (
          s.name_primary_language.toLowerCase().includes(term.filter.toLowerCase())
          || s.name_secondary_language.toLowerCase().includes(term.filter.toLowerCase())
          || s.subscription_category.toLowerCase().includes(term.filter.toLowerCase())
        ) && (
          ((s.recurring_service_catagories) && s.recurring_service_catagories.includes(term.serviceTypeFilter))
          || ((s.non_recurring_service_catagories) && s.non_recurring_service_catagories.includes(term.serviceTypeFilter))
        )
      );
    }
    else if (term.filter) {
      result = plan.filter((s: any) =>
        s.name_primary_language.toLowerCase().includes(term.filter.toLowerCase())
        || s.name_secondary_language.toLowerCase().includes(term.filter.toLowerCase())
        || s.subscription_category.toLowerCase().includes(term.filter.toLowerCase())
      );
    }
    else if (term.serviceTypeFilter) {
      result = plan.filter(
         (s: any) =>
         ((s.recurring_service_catagories) && s.recurring_service_catagories.includes(term.serviceTypeFilter))
        || ((s.non_recurring_service_catagories) && s.non_recurring_service_catagories.includes(term.serviceTypeFilter))
      );
    }
    return result;
  }
}
@Pipe({
  name: 'chargeFilter',
  standalone: true
})
export class ChargeFilterPipe implements PipeTransform {
  constructor() { }

  transform(charge: any, term: any): any[] {
    //check if the search term is defined
    if (!charge || !term) return charge;
    if (!term.filter && !term.chargeTypeFilter) return charge;
    let result = {} as any[];
    if (term.filter && term.chargeTypeFilter) {
      result = charge.filter((s: any) =>
        (
          s.name_primary_language.toLowerCase().includes(term.filter.toLowerCase())
          || s.name_secondary_language.toLowerCase().includes(term.filter.toLowerCase())
          || s.charge_category_name_primary_language.toLowerCase().includes(term.filter.toLowerCase())
          || s.charge_category_name_secondary_language.toLowerCase().includes(term.filter.toLowerCase())
        ) &&
          ((s.charge_category_id) && s.charge_category_id == term.chargeTypeFilter)
      );
    }
    else if (term.filter) {
      result = charge.filter((s: any) =>
        s.name_primary_language.toLowerCase().includes(term.filter.toLowerCase())
        || s.name_secondary_language.toLowerCase().includes(term.filter.toLowerCase())
        || s.charge_category_name_primary_language.toLowerCase().includes(term.filter.toLowerCase())
        || s.charge_category_name_secondary_language.toLowerCase().includes(term.filter.toLowerCase())
      );
    }
    else if (term.chargeTypeFilter) {
      result = charge.filter(
         (s: any) =>
         ((s.charge_category_id) && s.charge_category_id == term.chargeTypeFilter)
      );
    }
    return result;
  }
}


@Pipe({
  name: 'addonFilter',
  standalone: true
})
export class AddonFilterPipe implements PipeTransform {
  constructor() { }

  transform(charge: PlanAddons[], term: any): any[] {
    //check if the search term is defined
    if (!charge || !term) return charge;
    if (!term.filter) return charge;
    let result = {} as any[];
    if (term.filter) {
      result = charge.filter((s: PlanAddons) =>
        (
          s.name_primary_language.toLowerCase().includes(term.filter.toLowerCase())
          || s.name_secondary_language.toLowerCase().includes(term.filter.toLowerCase())
          || s.service_name_primary_language.toLowerCase().includes(term.filter.toLowerCase())
          || s.service_name_secondary_language.toLowerCase().includes(term.filter.toLowerCase())
          || s.plan_name_primary_language.toLowerCase().includes(term.filter.toLowerCase())
          || s.plan_name_secondary_language.toLowerCase().includes(term.filter.toLowerCase())
        )
      );
    }
    else if (term.filter) {
      result = charge.filter((s: PlanAddons) =>
      s.name_primary_language.toLowerCase().includes(term.filter.toLowerCase())
      || s.name_secondary_language.toLowerCase().includes(term.filter.toLowerCase())
      || s.service_name_primary_language.toLowerCase().includes(term.filter.toLowerCase())
      || s.service_name_secondary_language.toLowerCase().includes(term.filter.toLowerCase())
      || s.plan_name_primary_language.toLowerCase().includes(term.filter.toLowerCase())
      || s.plan_name_secondary_language.toLowerCase().includes(term.filter.toLowerCase())
      );
    }
    return result;
  }
}

@Pipe({
  name: 'clientfilter',
  standalone: true
})
export class ClientFilterPipe implements PipeTransform {
  constructor() { }

  transform(clients: any[], term: string): any {
    //check if the search term is defined
    if (!clients || !term) return clients;
    //return updated setting array
    const result = clients.filter(s => s.name.toLowerCase().includes(term.toLowerCase()) || term.includes(s.id));
    return result;
  }
}


@Pipe({
  name: 'tenantfilter',
  standalone: true
})
export class TenantFilterPipe implements PipeTransform {
  constructor() { }
  transform(tenants: any[], term: string): any {
    //check if the search term is defined
    if (!tenants || !term) return tenants;
    const result = tenants.filter(s => s.name_primary_language.toLowerCase().includes(term.toLowerCase()) 
     || s.name_secondary_language.toLowerCase().includes(term.toLowerCase())
     || term.includes(s.id));
    return result;
  }
}

@Pipe({
  name: 'automationFilter',
  standalone: true
})
export class AutomationFilterPipe implements PipeTransform {

  transform(automations: any[], term: string): any {
    // Check if search term is defined
    if (!automations || !term) return automations;

    // Filter and update counts
    return automations.map(category => {
      // Filter events that match the search term
      const filteredEvents = category.events.filter((event: any) => 
        event.friendly_name.toLowerCase().includes(term.toLowerCase()) || 
        event.description.toLowerCase().includes(term.toLowerCase())
      );

      // Only include categories that have matching events or category name matches
      if (filteredEvents.length > 0 || category.category_name.toLowerCase().includes(term.toLowerCase())) {
        return {
          ...category,
          events: filteredEvents,
          count: filteredEvents.length // Update count to reflect filtered results
        };
      }
      return null;
    }).filter(category => category !== null); // Remove categories with no matches
  }
}

@Pipe({
  name: 'automationCategoryFilter',
  standalone: true
})
export class AutomationCategoryFilterPipe implements PipeTransform {
  constructor() { } 

  transform(automations: AutomationMaster[], categoryValue: number): any {
    // Check if automations exist
    if (!automations || categoryValue == null) return automations;
    
    // Return filtered automations by category
    return automations.filter(automation => 
      automation.category === categoryValue
    );
  }
}

;
